import { z } from "zod";

import { TaskBreakDownData } from "./task-and-claims";
import { weightScaleBreakdown } from "./weigh-scale-ticket.schema";

import { dateValidator } from "@/lib/validators";

export const resourceRecoveryDetailsSchema = z.object({
  id: z.string().optional(),
  usedTickets: z.array(z.string()).optional(),
  deletedUsedTickets: z.array(z.string()).optional(),
  tippingRecords: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
  manualData: z.array(
    z.object({
      id: z.string().optional(),
      tippingId: z.string().optional(),
      ticketNumber: z.coerce.number().optional(),
      loadType: z.object({
        id: z.string(),
        quantity: z.coerce.number(),
      }),
      wasteType: z.object({
        id: z.string(),
        weight: z.coerce.number(),
      }),
      breakDown: weightScaleBreakdown,
    }),
  ),
});

const taskBreakdownsSchema = z
  .array(
    z
      .object({
        approvedEstimateId: z.string().uuid(),
        claimsId: z.string().uuid(),
        todayProgress: z.coerce.number().min(0).max(100).default(0).optional(),
        taskBreakdown: z.array(TaskBreakDownData),
      })
      .optional(),
  )
  .superRefine((data) => {
    data.splice(0, data.length, ...data.filter((item) => item));
  });

export const breakDown = z.object({
  id: z.string().optional(),
  title: z.string().optional(),
  shiftId: z.string(),
  time: z.coerce.number(),
});

export const location = z.object({
  breakStartId: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  employeeDaySheetId: z.string().optional(),
  employeeWorkHourId: z.string().nullable().optional(),
  id: z.string().optional(),
  lat: z.number().optional(),
  long: z.number().optional(),
  type: z.string().optional(),
  updatedAt: z.string().optional(),
});

const WorkingHourSchema = z.object({
  id: z.string().optional(),
  startTime: z.union([z.string(), z.date(), z.number()]),
  endTime: z.union([z.string(), z.date(), z.number()]),
  break: z.coerce.number(),
  files: z
    .array(
      z
        .instanceof(File, {
          message: "Required",
        })
        .or(z.string())
        .optional(),
    )
    .optional(),
  note: z.string().optional(),
  workBreakDown: z.array(breakDown),
  location: z.array(location).optional(),
  totalHours: z.coerce.number(),
});

export const EmployeeWorkingHourSchema = WorkingHourSchema.extend({
  employeeId: z.string(),
});
export const SubcontractorWorkingHourSchema = WorkingHourSchema.extend({
  subcontractorId: z.string(),
});

export const DailyReport = z.object({
  // fot update
  deleteEmployeeWorkingHours: z.array(z.string()).optional(),
  deleteSubcontractorWorkingHours: z.array(z.string()).optional(),
  deleteWeighScaleTicketItems: z.array(z.string()).optional(),

  id: z.string().uuid().optional(),
  date: dateValidator(true, "Required"),
  customerId: z
    .string()
    .min(2, {
      message: "Required",
    })
    .max(50),
  projectId: z
    .string()
    .min(1, {
      message: "Required",
    })
    .max(50),
  ticketId: z.string().optional(),
  employeeWorkingHours: z.array(EmployeeWorkingHourSchema).optional(),
  subcontractorWorkingHours: z.array(SubcontractorWorkingHourSchema).optional(),
  resourceRecoveryDetails: resourceRecoveryDetailsSchema.optional(),
  taskBreakdowns: taskBreakdownsSchema.optional(),
});

export type TDailyReport = z.infer<typeof DailyReport>;

export type TEmployeeWorkingHour = z.infer<typeof EmployeeWorkingHourSchema>;
